import React, { ReactNode, useState, useEffect } from 'react';
import {
  Home,
  Users,
  Briefcase,
  Compass,
  Heart,
  Settings,
  List as ListIcon,
  Handshake,
  Ticket,
  Lock
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '@/hooks/useAdmin';
import { toast } from 'sonner';

const AdminLayout = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const { logoutAdmin, loading: loggingOut } = useAdmin();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    try {
      await logoutAdmin();
      navigate('/administrator/login');
    } catch (error) {
      toast.error('Failed to log out');
    }
  };

  const menuItems = [
    { label: 'Dashboard', path: '/administrator/dashboard', icon: <Home size={20} /> },
    { label: 'Silver Users', path: '/administrator/silver-users', icon: <Users size={20} /> },
    { label: 'Career Counselors', path: '/administrator/career-counselors', icon: <Briefcase size={20} /> },
    { label: 'Domain Experts', path: '/administrator/domain-experts', icon: <Compass size={20} /> },
    { label: 'Psychologists', path: '/administrator/psychologists', icon: <Heart size={20} /> },
    { label: 'Listings', path: '/administrator/listings', icon: <ListIcon size={20} /> },
    { label: 'Partners', path: '/administrator/manage-partners', icon: <Handshake size={20} /> },
    { label: 'Coupons', path: '/administrator/coupon-management', icon: <Ticket size={20} /> },
    { label: 'Settings', path: '/administrator/setup', icon: <Settings size={20} /> },
    { label: 'Change Password', path: '/administrator/change-password', icon: <Lock size={20} /> },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="flex h-screen">
        {/* Sidebar */}
        {isSidebarOpen && (
          <div className="bg-gray-800 text-white w-64 flex-shrink-0">
            <div className="p-4">
              <h1 className="text-2xl font-semibold">Admin Panel</h1>
            </div>
            <nav className="mt-4">
              {menuItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.path}
                  className="flex items-center p-4 hover:bg-gray-700"
                >
                  {item.icon}
                  <span className="ml-2">{item.label}</span>
                </Link>
              ))}
            </nav>
            <div className="p-4 mt-auto">
              <button
                onClick={handleLogout}
                disabled={loggingOut}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded w-full disabled:opacity-50"
              >
                {loggingOut ? 'Logging Out...' : 'Logout'}
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <header className="bg-white shadow">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center">
                <button
                  className="lg:hidden text-gray-500 focus:outline-none focus:text-gray-700"
                  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <h2 className="text-xl font-semibold ml-2">Dashboard</h2>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
            <div className="container mx-auto p-4">
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
